import { useRef, useEffect, useState } from 'react'
import {
  Download, Calendar, Share2, CheckCircle2, User, Briefcase, Target,
  Clock, MapPin, AlertCircle, CreditCard, Smartphone, DollarSign, ShieldCheck, X,
  Upload, Copy, Check, FileText, ChevronUp
} from 'lucide-react'
import QRCodeLib from 'qrcode'
import { formatScheduleFallback, SCHEDULE_SERIES_NAME } from '../../utils/masterclassSchedule'
import { masterclassService } from '../../services/masterclassService'
import { exportElementPDF, exportElementPNG } from '../../utils/pdfExport'
import {
  trackMasterclassPaymentStarted,
  trackMasterclassPaymentCompleted,
  trackMasterclassPaymentProofSubmitted,
  getTrackingContext
} from '../../services/websiteTracker'
import LoadingSpinner from '../LoadingSpinner'

// ─── QR Code ────────────────────────────────────────────────────────────────
function QRCode({ value, size = 160, lightColor = '#ffffff' }) {
  const [dataUrl, setDataUrl] = useState('')

  useEffect(() => {
    if (!value) return
    QRCodeLib.toDataURL(value, {
      width: size,
      margin: 1,
      color: { dark: '#183B56', light: lightColor },
      errorCorrectionLevel: 'H',
    })
      .then((url) => setDataUrl(url))
      .catch(() => setDataUrl(''))
  }, [value, size, lightColor])

  if (!dataUrl) {
    return <div className="bg-transparent" style={{ width: size, height: size }} />
  }

  return <img src={dataUrl} alt="QR Code" className="rounded-none" style={{ width: size, height: size, imageRendering: 'pixelated' }} />
}

function formatDate(dateStr) {
  if (!dateStr) return formatScheduleFallback()
  return new Date(dateStr).toLocaleDateString('en-UG', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
}

function normalizeRegistration(raw) {
  if (!raw) return {}
  const base = raw.registration || raw
  const mc = base.masterclass || {}
  const top = {
    topic: base.topic,
    event_date: base.event_date,
    start_time: base.start_time,
    venue: base.venue,
    speaker_name: base.speaker_name,
    speaker_title: base.speaker_title,
    speaker_company: base.speaker_company,
  }
  const full = base.name || base.full_name || base.attendee_name || ''
  const [first = '', ...rest] = full.split(/\s+/)
  return {
    ...base,
    first_name: base.first_name || base.registrant_first_name || base.attendee_first_name || first,
    last_name: base.last_name || base.registrant_last_name || base.attendee_last_name || rest.join(' '),
    masterclass: {
      topic: mc.topic || top.topic,
      event_date: mc.event_date || top.event_date,
      start_time: mc.start_time || top.start_time,
      venue: mc.venue || top.venue,
      speaker_name: mc.speaker_name || top.speaker_name,
      speaker_title: mc.speaker_title || top.speaker_title,
      speaker_company: mc.speaker_company || top.speaker_company,
    },
  }
}

export default function DigitalEventPass({ registration, mode = 'attendee' }) {
  const isPreview = mode === 'preview'
  const passRef = useRef(null)
  const [downloaded, setDownloaded] = useState(false)
  const [regState, setRegState] = useState(() => normalizeRegistration(registration))
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState('mobile_money')
  const [paymentPhone, setPaymentPhone] = useState(registration?.phone || '')
  const [txRef, setTxRef] = useState(registration?.payment_reference || '')
  const [proofFile, setProofFile] = useState(null)
  const [payNotes, setPayNotes] = useState('')
  const [paying, setPaying] = useState(false)
  const [payError, setPayError] = useState('')
  const [paySuccess, setPaySuccess] = useState('')
  const [copiedNumber, setCopiedNumber] = useState(false)
  const [copiedRef, setCopiedRef] = useState(false)

  useEffect(() => {
    if (registration) setRegState(normalizeRegistration(registration))
  }, [registration])

  useEffect(() => {
    if (!regState.registration_number) return
    const hasName = regState.first_name || regState.last_name
    const hasSpeaker = regState.masterclass?.speaker_name
    if (!hasName || !hasSpeaker) {
      masterclassService.verifyRegistration(regState.registration_number)
        .then((data) => {
          if (data?.registration) {
            setRegState((prev) => ({ ...prev, ...normalizeRegistration(data.registration) }))
          }
        })
        .catch(() => {})
    }
  }, [regState.registration_number, regState.first_name, regState.last_name, regState.masterclass?.speaker_name])

  const {
    id: regId,
    registration_number,
    first_name,
    last_name,
    entitlement_type = 'FREE',
    payment_status = 'NOT_REQUIRED',
    rejection_reason = '',
    proof_submission_token,
    pass_token,
    masterclass = {},
  } = regState || {}
  const { topic, event_date, start_time, venue, speaker_name, speaker_title, speaker_company } = masterclass || {}

  const isGuest = entitlement_type === 'PAYMENT_REQUIRED'
  const isPaid = payment_status === 'COMPLETED'
  const isPendingVerification = isGuest && payment_status === 'PENDING_VERIFICATION'
  const isRejected = isGuest && payment_status === 'REJECTED'
  const isPending = isGuest && (!payment_status || payment_status === 'PENDING')
  const isPaymentPending = isGuest && !isPaid

  const handleOpenPaymentModal = () => {
    setPayError('')
    setPaySuccess('')
    setShowPaymentModal(true)
    trackMasterclassPaymentStarted(masterclass?.id, registration_number, paymentMethod)
  }

  const handleCopy = (text, type) => {
    if (!text) return
    navigator.clipboard?.writeText(text)
    if (type === 'number') {
      setCopiedNumber(true)
      setTimeout(() => setCopiedNumber(false), 2000)
    } else if (type === 'ref') {
      setCopiedRef(true)
      setTimeout(() => setCopiedRef(false), 2000)
    }
  }

  const handleSubmitProofOrPayment = async (e) => {
    e.preventDefault()
    setPaying(true)
    setPayError('')
    setPaySuccess('')

    try {
      const trackingCtx = getTrackingContext ? getTrackingContext() : {}

      const formData = new FormData()
      if (regId) formData.append('registration_id', regId)
      if (registration_number) formData.append('registration_number', registration_number)
      formData.append('payment_method', paymentMethod)
      formData.append('phone', paymentPhone)
      if (txRef) formData.append('transaction_reference', txRef)
      if (payNotes) formData.append('notes', payNotes)
      if (proofFile) formData.append('proof_file', proofFile)
      if (trackingCtx.visitorId) formData.append('visitor_id', trackingCtx.visitorId)
      if (trackingCtx.sessionId) formData.append('session_id', trackingCtx.sessionId)

      const res = await masterclassService.submitPaymentProof(formData, proof_submission_token)

      if (res.success) {
        setPaySuccess('Payment proof submitted successfully! SMA Finance is verifying your submission.')
        setRegState((prev) => ({
          ...prev,
          payment_status: 'PENDING_VERIFICATION',
          payment_reference: txRef || prev.payment_reference,
          rejection_reason: null,
        }))
        trackMasterclassPaymentProofSubmitted(
          masterclass?.id,
          registration_number,
          paymentMethod
        )
        setTimeout(() => {
          setShowPaymentModal(false)
        }, 2200)
      } else {
        setPayError(res.error || 'Failed to submit payment proof. Please try again.')
      }
    } catch (err) {
      setPayError(err.message || 'Payment submission failed')
    } finally {
      setPaying(false)
    }
  }

  const handleDownloadPNG = async () => {
    if (!passRef.current) return
    try {
      await exportElementPNG(
        passRef.current,
        `SMA-Pass-${registration_number || 'PASS'}.png`,
        { scale: 3, backgroundColor: '#FAFAF8' },
      )
      setDownloaded(true)
    } catch (err) {
      console.error('Download failed', err)
    }
  }

  const handleDownloadPDF = async () => {
    if (!passRef.current) return
    try {
      await exportElementPDF(
        passRef.current,
        `SMA-Pass-${registration_number || 'PASS'}.pdf`,
        { orientation: 'portrait', format: [85, 135], backgroundColor: '#FAFAF8' },
      )
    } catch (err) {
      console.error('PDF failed', err)
    }
  }

  const [calendarOpen, setCalendarOpen] = useState(false)

  const handleAddToGoogleCalendar = () => {
    const start = event_date ? new Date(event_date) : new Date()
    start.setHours(17, 0, 0, 0)
    const end = new Date(start.getTime() + 2 * 3600000)
    const fmt = (d) => d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z'
    const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent('SMA Friday Masterclass')}&dates=${fmt(start)}/${fmt(end)}&details=${encodeURIComponent(`Registration: ${registration_number}\nTopic: ${topic || 'TBA'}\nSpeaker: ${speaker_name || 'TBA'}`)}&location=${encodeURIComponent(venue || 'Synergy Multimedia Academy, Kampala')}`
    window.open(url, '_blank')
    setCalendarOpen(false)
  }

  const handleDownloadIcal = () => {
    const start = event_date ? new Date(event_date) : new Date()
    start.setHours(17, 0, 0, 0)
    const end = new Date(start.getTime() + 2 * 3600000)
    const pad = (n) => String(n).padStart(2, '0')
    const fmt = (d) => `${d.getUTCFullYear()}${pad(d.getUTCMonth()+1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`
    const uid = `${registration_number || 'SMA'}-${Date.now()}@synergymultimediaacademy.com`
    const ics = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Synergy Multimedia Academy//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      `UID:${uid}`,
      `DTSTAMP:${fmt(new Date())}`,
      `DTSTART:${fmt(start)}`,
      `DTEND:${fmt(end)}`,
      `SUMMARY:SMA Friday Masterclass${topic ? ` — ${topic}` : ''}`,
      `DESCRIPTION:Registration: ${registration_number || ''}\nSpeaker: ${speaker_name || 'TBA'}\nTopic: ${topic || 'TBA'}`,
      `LOCATION:${venue || 'Synergy Multimedia Academy, Kampala, Uganda'}`,
      'END:VEVENT',
      'END:VCALENDAR',
    ].join('\r\n')
    const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `SMA-Masterclass-${registration_number || 'Pass'}.ics`
    a.click()
    URL.revokeObjectURL(url)
    setCalendarOpen(false)
  }

  const handleShare = async () => {
    const text = `I'm attending The SMA Monthly Masterclass! Topic: "${topic}" with ${speaker_name}. See you on the last Friday of the month! #SMAMasterclass #CreativeTalk`
    if (navigator.share) {
      try { await navigator.share({ title: SCHEDULE_SERIES_NAME, text, url: window.location.origin + '/masterclass' }) }
      catch {}
    } else {
      await navigator.clipboard.writeText(text)
      alert('Copied to clipboard!')
    }
  }

  const speakerRoleText = [speaker_title, speaker_company].filter(Boolean).join(' · ')

  return (
    <div className="space-y-6">
      {/* ── Attendee-only alert banners (hidden in preview mode) ── */}
      {!isPreview && isPendingVerification && (
        <div className="bg-sky-500/10 border border-sky-500/30 p-4 max-w-[480px] mx-auto rounded-none">
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-sky-400 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-bold text-sky-300 font-sans uppercase tracking-wider">
                Payment Proof Pending Verification
              </h4>
              <p className="text-xs text-gray-300 font-sans mt-1">
                Your payment proof was received and is currently being verified by SMA Finance. Your QR pass will activate as <strong className="text-white">Confirmed</strong> upon verification.
              </p>
              <button
                onClick={handleOpenPaymentModal}
                className="mt-3 flex items-center justify-center gap-2 w-full py-2 px-3 bg-sky-500/20 hover:bg-sky-500/30 text-sky-300 border border-sky-500/40 font-sans font-bold text-xs uppercase tracking-widest transition-all rounded-none"
              >
                <Upload className="w-4 h-4" /> Update / Re-submit Payment Proof
              </button>
            </div>
          </div>
        </div>
      )}

      {!isPreview && isRejected && (
        <div className="bg-red-500/10 border border-red-500/40 p-4 max-w-[480px] mx-auto rounded-none">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-bold text-red-300 font-sans uppercase tracking-wider">
                Payment Proof Rejected
              </h4>
              <p className="text-xs text-gray-300 font-sans mt-1">
                {rejection_reason ? (
                  <><strong>Reason:</strong> {rejection_reason}</>
                ) : (
                  'The uploaded payment proof could not be verified. Please submit a valid transaction screenshot or reference.'
                )}
              </p>
              <button
                onClick={handleOpenPaymentModal}
                className="mt-3 flex items-center justify-center gap-2 w-full py-2.5 px-4 bg-red-500 hover:bg-red-600 text-white font-sans font-bold text-xs uppercase tracking-widest transition-all rounded-none"
              >
                <Upload className="w-4 h-4" /> Re-submit Payment Proof
              </button>
            </div>
          </div>
        </div>
      )}

      {!isPreview && isPending && (
        <div className="bg-[#EE8D21]/10 border border-[#EE8D21]/40 p-4 max-w-[480px] mx-auto rounded-none">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-[#EE8D21] shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-bold text-white font-sans uppercase tracking-wider">
                Guest Pass · Payment Required
              </h4>
              <p className="text-xs text-gray-300 font-sans mt-1">
                Guest tickets are <strong className="text-white">UGX 150,000</strong>. Pay to SMA Mobile Money (<span className="font-mono text-[#EE8D21]">0763875564</span>) and upload your proof to validate admission.
              </p>
              <button
                onClick={handleOpenPaymentModal}
                className="mt-3 flex items-center justify-center gap-2 w-full py-2.5 px-4 bg-[#EE8D21] hover:bg-[#d97d19] text-black font-sans font-bold text-xs uppercase tracking-widest transition-all rounded-none"
              >
                <Smartphone className="w-4 h-4" /> Pay UGX 150,000 / Submit Proof
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════
           THE PASS — redesigned, topic as hero, minimal decoration
           passRef wraps ONLY the pass card for PDF/PNG export
      ════════════════════════════════════════════════════════════════════ */}
      <div ref={passRef} className="digital-pass-export relative overflow-hidden rounded-none bg-[#FAFAF8] max-w-[420px] mx-auto print:max-w-none">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E5E5]">
          <div className="flex items-center gap-3 min-w-0">
            <img src="/Logos/SMA-Logo-Dark.png" alt="SMA" className="h-8 w-auto object-contain" />
            <div className="min-w-0">
              <div className="text-[10px] font-bold tracking-[0.18em] text-[#1C1C1A] uppercase leading-tight font-sans">Synergy Multimedia Academy</div>
            </div>
          </div>
          <div className="text-[10px] font-medium tracking-[0.2em] text-[#1C1C1A] uppercase font-sans">Digital Pass</div>
        </div>

        {/* Body */}
        <div className="px-5 py-6 sm:px-6 sm:py-7">
          {/* Series eyebrow */}
          <div className="text-[10px] font-medium tracking-[0.22em] text-[#A88445] uppercase mb-3 font-sans">
            The Creative Talk Series
          </div>

          {/* TOPIC — the hero */}
          <h1 className="text-[20px] sm:text-[24px] font-bold text-[#183B56] leading-[1.15] tracking-tight font-sans mb-2">
            {topic || 'SMA Masterclass Session'}
          </h1>

          {/* Speaker */}
          <div className="pb-5 mb-5 border-b border-[#E5E5E5]">
            <div className="text-[13px] font-semibold text-[#183B56] font-sans leading-tight">
              {speaker_name || 'TBA'}
            </div>
            {speakerRoleText && (
              <div className="text-[11px] text-[#727272] font-sans mt-0.5 leading-snug">
                {speakerRoleText}
              </div>
            )}
          </div>

          {/* Date · Time · Venue — compact grid */}
          <div className="grid grid-cols-3 gap-4 mb-5 pb-5 border-b border-[#E5E5E5]">
            <div className="min-w-0">
              <div className="text-[9px] tracking-[0.15em] text-[#727272] uppercase font-sans mb-1">Date</div>
              <div className="text-[11px] font-semibold text-[#183B56] font-sans leading-snug">
                {formatDate(event_date)}
              </div>
            </div>
            <div className="min-w-0">
              <div className="text-[9px] tracking-[0.15em] text-[#727272] uppercase font-sans mb-1">Time</div>
              <div className="text-[11px] font-semibold text-[#183B56] font-sans leading-snug">
                {start_time || '17:00'}
              </div>
            </div>
            <div className="min-w-0">
              <div className="text-[9px] tracking-[0.15em] text-[#727272] uppercase font-sans mb-1">Venue</div>
              <div className="text-[11px] font-semibold text-[#183B56] font-sans leading-snug">
                {venue || 'Synergy Multimedia Academy, Makerere - Kubiri'}
              </div>
            </div>
          </div>

          {/* Attendee */}
          <div className="pb-5 mb-5 border-b border-[#E5E5E5]">
            <div className="text-[9px] tracking-[0.15em] text-[#727272] uppercase font-sans mb-1">Attendee</div>
            <div className="text-[17px] font-bold text-[#183B56] font-sans leading-tight">
              {`${first_name || ''} ${last_name || ''}`.trim() || '—'}
            </div>
          </div>

          {/* QR + Registration + Status */}
          <div className="flex items-start gap-4">
            <div className="shrink-0">
              <QRCode
                value={pass_token ? `${window.location.origin}/event-check-in?pass=${encodeURIComponent(pass_token)}` : (registration_number ? `${window.location.origin}/event-check-in?reg=${encodeURIComponent(registration_number)}` : '')}
                size={108}
                lightColor="#FAFAF8"
              />
            </div>
            <div className="flex-1 min-w-0 pt-0.5">
              <div className="text-[9px] tracking-[0.15em] text-[#727272] uppercase font-sans mb-1">Registration ID</div>
              <div className="text-[14px] font-semibold text-[#183B56] tracking-wider font-mono break-all mb-2">
                {registration_number || '—'}
              </div>
              <div className="text-[11px] font-medium font-sans" style={{ color: (!isGuest || isPaid) ? '#22c55e' : isRejected ? '#ef4444' : '#727272' }}>
                {isGuest
                  ? (isPaid ? 'Guest admission confirmed.' : isRejected ? 'Admission not confirmed.' : 'Payment pending.')
                  : 'Student admission confirmed.'}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center py-4 border-t border-[#E5E5E5]">
          <span className="block text-[9px] tracking-[0.25em] text-[#727272] uppercase font-sans">Learn. Connect. Grow.</span>
          <span className="block text-[9px] text-[#727272] font-sans mt-1">synergymultimediaacademy.com</span>
        </div>
      </div>

      {/* ── Attendee-only action buttons (hidden in preview mode) ── */}
      {!isPreview && (
        <div className="relative">
          {/* Calendar picker popover */}
          {calendarOpen && (
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-10 bg-cinema-charcoal border border-white/10 shadow-2xl min-w-[200px] text-sm">
              <div className="px-3 pt-3 pb-1">
                <p className="text-[10px] uppercase tracking-widest text-cinema-muted font-sans mb-2">Add to Calendar</p>
              </div>
              <button
                onClick={handleAddToGoogleCalendar}
                className="w-full flex items-center gap-3 px-4 py-3 text-cinema-soft hover:text-cinema-gold hover:bg-white/5 transition-colors font-sans text-xs text-left"
              >
                <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><path d="M7 2v2M17 2v2M2 9h20" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                Google Calendar
              </button>
              <button
                onClick={handleDownloadIcal}
                className="w-full flex items-center gap-3 px-4 py-3 text-cinema-soft hover:text-cinema-gold hover:bg-white/5 transition-colors border-t border-white/5 font-sans text-xs text-left"
              >
                <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
                Download .ics (Apple / Outlook)
              </button>
              <button
                onClick={() => setCalendarOpen(false)}
                className="w-full flex items-center gap-3 px-4 py-3 text-cinema-muted hover:text-white hover:bg-white/5 transition-colors border-t border-white/5 font-sans text-xs text-left"
              >
                <X className="w-4 h-4 shrink-0" />
                Cancel
              </button>
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/5 overflow-hidden rounded-none">
            {[{fn: handleDownloadPNG, Icon: Download, label: 'Download PNG'},
              {fn: handleDownloadPDF, Icon: Download, label: 'Download PDF'},
              {fn: () => setCalendarOpen((v) => !v), Icon: Calendar, label: calendarOpen ? 'Close' : 'Add to Calendar', active: calendarOpen},
              {fn: handleShare, Icon: Share2, label: 'Share Event'},
            ].map(({ fn, Icon, label, active }) => (
              <button key={label} onClick={fn}
                className={`flex flex-col items-center gap-2 px-3 py-5 bg-cinema-black/40 hover:bg-cinema-charcoal/80 transition-all duration-300 group rounded-none ${
                  active ? 'text-cinema-gold bg-cinema-charcoal/60' : 'text-cinema-muted hover:text-cinema-gold'
                }`}>
                <Icon className="w-5 h-5" />
                <span className="text-[10px] uppercase tracking-[0.15em] font-sans">{label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {!isPreview && downloaded && (
        <div className="flex items-center gap-2 text-cinema-gold text-sm font-sans">
          <CheckCircle2 className="w-4 h-4" /> Pass downloaded successfully
        </div>
      )}

      {/* ── Payment Checkout & Proof Submission Modal (attendee-only) ── */}
      {!isPreview && showPaymentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="bg-cinema-black border border-white/10 w-full max-w-lg p-6 sm:p-8 space-y-6 text-white shadow-2xl relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setShowPaymentModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <div>
              <span className="text-xs uppercase tracking-widest text-[#EE8D21] font-sans font-bold">
                Masterclass Payment
              </span>
              <h3 className="text-xl font-bold text-white font-display mt-1">
                Guest Pass Payment & Proof Submission
              </h3>
              <p className="text-xs text-gray-400 font-sans mt-1">
                Required Fee: <strong className="text-white text-sm">UGX 150,000</strong> · Ref: <span className="font-mono text-gray-300">{registration_number}</span>
              </p>
            </div>

            {/* Payment Method Selector */}
            <div className="grid grid-cols-3 gap-2">
              {[
                { id: 'mobile_money', label: 'Mobile Money', icon: Smartphone },
                { id: 'bank_transfer', label: 'Bank Transfer', icon: CreditCard },
                { id: 'cash', label: 'Cash / Door', icon: DollarSign },
              ].map((m) => {
                const Icon = m.icon
                const active = paymentMethod === m.id
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setPaymentMethod(m.id)}
                    className={`flex flex-col items-center gap-2 p-3 border text-center transition-all ${
                      active
                        ? 'border-[#EE8D21] bg-[#EE8D21]/10 text-white'
                        : 'border-white/10 bg-white/5 text-gray-400 hover:text-white'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="text-[10px] uppercase tracking-wider font-sans font-bold">{m.label}</span>
                  </button>
                )
              })}
            </div>

            <form onSubmit={handleSubmitProofOrPayment} className="space-y-4">
              {paymentMethod === 'mobile_money' && (
                <div className="space-y-4 bg-white/5 p-4 border border-white/10">
                  <div className="text-xs text-gray-300 font-sans space-y-2">
                    <p className="font-semibold text-white">SMA Mobile Money Payment Instructions:</p>
                    <div className="p-3 bg-black/40 border border-white/10 space-y-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-[10px] text-gray-400 uppercase font-sans">Mobile Money Number (Airtel / MTN)</p>
                          <p className="text-sm font-mono font-bold text-cinema-glow">0763875564</p>
                          <p className="text-[10px] text-gray-400">Account Name: <strong>Kiggundu Yacob</strong> (Synergy Multimedia Academy)</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleCopy('0763875564', 'number')}
                          className="flex items-center gap-1 text-[10px] uppercase px-2.5 py-1.5 bg-white/10 hover:bg-white/20 text-white transition-colors"
                        >
                          {copiedNumber ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
                          {copiedNumber ? 'Copied' : 'Copy'}
                        </button>
                      </div>

                      <div className="border-t border-white/10 pt-2 flex items-center justify-between">
                        <div>
                          <p className="text-[10px] text-gray-400 uppercase font-sans">Payment Reason / Reference</p>
                          <p className="text-xs font-mono font-bold text-white">{registration_number}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleCopy(registration_number, 'ref')}
                          className="flex items-center gap-1 text-[10px] uppercase px-2.5 py-1.5 bg-white/10 hover:bg-white/20 text-white transition-colors"
                        >
                          {copiedRef ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
                          {copiedRef ? 'Copied' : 'Copy'}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] uppercase tracking-wider text-gray-400 font-sans mb-1">
                      Upload Payment Screenshot / Proof Slip (Image or PDF)
                    </label>
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={(e) => setProofFile(e.target.files?.[0] || null)}
                      className="w-full text-xs text-gray-300 file:mr-3 file:py-1.5 file:px-3 file:border-0 file:text-xs file:font-sans file:bg-[#EE8D21] file:text-black hover:file:bg-[#d97d19] cursor-pointer bg-black/40 border border-white/10 p-2"
                    />
                    {proofFile && (
                      <p className="text-[11px] text-green-400 mt-1 flex items-center gap-1 font-sans">
                        <Check className="w-3 h-3" /> Selected: {proofFile.name}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-[10px] uppercase tracking-wider text-gray-400 font-sans mb-1">
                      Transaction ID / Reference
                    </label>
                    <input
                      type="text"
                      value={txRef}
                      onChange={(e) => setTxRef(e.target.value)}
                      placeholder="e.g. 1928374650 or MTN MoMo ID"
                      className="w-full bg-black/40 border-b border-white/20 px-3 py-2 text-sm text-white focus:outline-none focus:border-[#EE8D21]"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] uppercase tracking-wider text-gray-400 font-sans mb-1">
                      Your Phone Number
                    </label>
                    <input
                      type="text"
                      value={paymentPhone}
                      onChange={(e) => setPaymentPhone(e.target.value)}
                      placeholder="+256 700 000 000"
                      className="w-full bg-black/40 border-b border-white/20 px-3 py-2 text-sm text-white focus:outline-none focus:border-[#EE8D21]"
                    />
                  </div>
                </div>
              )}

              {paymentMethod === 'bank_transfer' && (
                <div className="bg-white/5 p-4 border border-white/10 space-y-4 text-xs text-gray-300 font-sans">
                  <p className="font-semibold text-white">Bank Transfer Details:</p>
                  <div className="p-3 bg-black/40 border border-white/10 space-y-1">
                    <p>Bank: <strong className="text-white">Standard Chartered Bank Uganda</strong></p>
                    <p>Account Number: <strong className="text-cinema-glow font-mono">0100823802202</strong></p>
                    <p>Account Name: <strong className="text-white">Joseph Sengendo</strong></p>
                    <p>Branch: <strong>Speke Road Branch, Kampala</strong></p>
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-wider text-gray-400 font-sans mb-1">
                      Upload Bank Slip / Receipt
                    </label>
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={(e) => setProofFile(e.target.files?.[0] || null)}
                      className="w-full text-xs text-gray-300 file:mr-3 file:py-1.5 file:px-3 file:border-0 file:text-xs file:font-sans file:bg-[#EE8D21] file:text-black hover:file:bg-[#d97d19] cursor-pointer bg-black/40 border border-white/10 p-2"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-wider text-gray-400 font-sans mb-1">
                      Deposit / Slip Reference
                    </label>
                    <input
                      type="text"
                      value={txRef}
                      onChange={(e) => setTxRef(e.target.value)}
                      placeholder="e.g. BNK-REF-9921"
                      className="w-full bg-black/40 border-b border-white/20 px-3 py-2 text-sm text-white focus:outline-none focus:border-[#EE8D21]"
                    />
                  </div>
                </div>
              )}

              {paymentMethod === 'cash' && (
                <div className="bg-white/5 p-4 border border-white/10 space-y-3 text-xs text-gray-300 font-sans">
                  <p className="font-semibold text-white">Cash / Reception Door Payment:</p>
                  <p>You can pay <strong>UGX 150,000</strong> in cash directly at the academy reception desk (Makerere Kubiri, KK Building) when you arrive.</p>
                  <p className="text-gray-400">Reception staff will verify and admit you instantly upon payment settlement.</p>
                </div>
              )}

              {payError && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 text-red-400 text-xs font-sans">
                  <AlertCircle className="w-4 h-4 shrink-0" /> {payError}
                </div>
              )}

              {paySuccess && (
                <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/20 text-green-400 text-xs font-sans">
                  <ShieldCheck className="w-4 h-4 shrink-0" /> {paySuccess}
                </div>
              )}

              <button
                type="submit"
                disabled={paying}
                className="w-full py-3 px-4 bg-[#EE8D21] hover:bg-[#d97d19] text-black font-sans font-bold text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-2 rounded-none"
              >
                {paying ? (
                  <LoadingSpinner size="sm" inline />
                ) : (
                  <>
                    <Upload className="w-4 h-4" /> Submit Payment Proof for Verification
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

